import React, { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import config from "../../config";
import { setAuthData } from "../../slices/authSlice"; // ensure this slice is updated to NOT use localStorage directly
import "./signin.css";
import shmLogo from "../../assets/images/logo1.jpeg";
import conferenceImg from "../../assets/images/conferenceImg.png";
import backimage from "../../assets/images/back-image.jpg";

const SignIn = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    if (token) {
      navigate("/profile");
    }
  }, [token, navigate]);

  return (
    <div className="signin-container">
      <div className="signin-text">
        <div className="signin-text-container">
          <img src={conferenceImg} alt="Informatica Logo" className="img" />
        </div>
      </div>
      <div className="signin-form-panel">
        <div className="background-image-container">
          <img src={backimage} alt="Background" />
        </div>
        <div className="signin-wrapper">
          <div className="signin-form-section">
            <div className="signin-logo">
              <img src={shmLogo} alt="Logo Placeholder" />
            </div>
            <UserLogin />
          </div>
        </div>
      </div>
    </div>
  );
};

const UserLogin = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Helper function to generate a random device id
  const generateRandomDeviceId = () => {
    return (
      Math.random().toString(36).substring(2) +
      Date.now().toString(36)
    );
  };

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    const { email, password } = formData;
    let validationErrors = {};

    if (!email) {
      validationErrors.email = "Email is required.";
    } else if (!validateEmail(email)) {
      validationErrors.email = "Enter a valid email.";
    }

    if (!password) {
      validationErrors.password = "Password is required.";
    }

    if (!captchaVerified) {
      validationErrors.captcha = "Please verify the captcha.";
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    // Generate a random device id
    const deviceId = generateRandomDeviceId();

    // Build the API URL by replacing placeholders with the device ID, email, and password
    const url = config.API_SIGNIN_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", encodeURIComponent(email))
      .replace("{Password}", encodeURIComponent(password));

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();
      const result = data.Table[0];

      if (result.Status === "Success") {
        const token = result.Column1;
        const eventId = result.EventID;
        const applicantId = result.ApplicantID;
        // Dispatch auth data. Redux Persist will store these values.
        dispatch(setAuthData({ token, deviceId, eventId, applicantId, userId: email }));
        navigate("/profile");
      } else if (result.Status === "Email not registered") {
        setErrors({ general: "Email not registered" });
      } else if (result.Status === "Email not verified") {
        alert("Verify your email first.");
      } else if (result.Status === "Email or password incorrect") {
        setErrors({ general: "Email or password incorrect" });
      } else {
        setErrors({ general: "Login failed. Please try again." });
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors({ general: "An error occurred. Please try again later." });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNavigate = () => {
    navigate("/signup");
  };

  const hanleforgotPass = () => {
    navigate("/forgotpass");
  };

  return (
    <form className="login-form" onSubmit={handleSignIn}>
      <h2>Login</h2>
      {errors.general && <div className="error-message">{errors.general}</div>}
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
        />
        {errors.email && <div className="error-message">{errors.email}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="password">Password</label>
        <input
          type="password"
          id="password"
          name="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
        />
        {errors.password && <div className="error-message">{errors.password}</div>}
      </div>
      <ReCAPTCHA
        sitekey={config.RECAPTCHA_SITE_KEY}
        onChange={handleCaptchaChange}
        className="g-recaptcha"
      />
      {errors.captcha && <div className="error-message">{errors.captcha}</div>}
      <button type="submit" className="login-button">
        Login
      </button>
      <div className="extra-buttons">
        <button type="button" className="alt-button" onClick={hanleforgotPass}>
          Forgot Password?
        </button>
        <button onClick={handleNavigate} type="button" className="alt-button">
          Sign Up
        </button>
      </div>
    </form>
  );
};

export default SignIn;
